<template>
  <div class="step step-2">
    <div v-if="inProgress">
      <v-progress-circular
        :size="44"
        :width="3"
        color="primary"
        indeterminate
      ></v-progress-circular>
      <h1>{{ $t('texts.waiting_validation') }}</h1>
      <p>{{ $t('texts.be_patient') }}</p>
    </div>
    <div :class="inProgress ? 'd-none' : ''">
      <validation-observer ref="step2Form" v-slot="{ }">
        <v-form>
          <validation-provider :name="$t('fields.name')" rules="required" v-slot="{ errors }">
            <v-text-field
              :label="$t('fields.name')"
              class="required"
              v-model="name"
              filled
              required
              :error-messages="errors"
              :disabled="!isEditableField('name')"
            ></v-text-field>
          </validation-provider>
          <validation-provider :name="$t('fields.surname')" rules="required" v-slot="{ errors }">
            <v-text-field
              :label="$t('fields.surname')"
              v-model="lastName"
              filled
              required
              class="required"
              :error-messages="errors"
              :disabled="!isEditableField('last_name')"
            ></v-text-field>
          </validation-provider>
          <v-dialog
             ref="dialogDateOfBirth"
             v-model="dateOfBirthModal"
             :return-value.sync="dateOfBirth"
             persistent
             width="290px"
           >
             <template v-slot:activator="{ on, attrs }">
               <validation-provider
                 :name="$t('fields.birthdate')"
                 rules="required"
               >
                <v-text-field
                  :label="$t('fields.birthdate')"
                  v-model="dateOfBirthFormatted"
                  append-icon="mdi-calendar"
                  filled
                  :readonly="isEditableField('date_of_birth')"
                  v-bind="attrs"
                  v-on="on"
                  class="required"
                  :disabled="!isEditableField('date_of_birth')"
                 ></v-text-field>
              </validation-provider>
             </template>
             <v-date-picker
              v-model="dateOfBirth"
              :locale="locale"
              :first-day-of-week="firstDayOfWeek"
              color="primary darken-1"
              scrollable
              ref="dateOfBirthPicker"
              :max="moment().format('YYYY-MM-DD')"
            >
               <v-spacer></v-spacer>
               <v-btn text color="primary darken-1" @click="dateOfBirthModal = false">{{$t('actions.cancel')}}</v-btn>
               <v-btn text color="primary darken-1" @click="$refs.dialogDateOfBirth.save(dateOfBirth)">{{$t('actions.accept')}}</v-btn>
             </v-date-picker>
          </v-dialog>

          <validation-provider
            :name="$t('fields.gender')"
            rules="required"
            v-slot="{ errors }"
          >
            <v-select
              :label="$t('fields.gender')"
              v-model="gender"
              :items="genders"
              item-text="name"
              :error-messages="errors"
              item-value="id"
              filled
              class="required"
              :disabled="!isEditableField('sex')"
            >
              <template v-slot:selection="{ item }">
                {{$t(`gender.${item.id}`)}}
              </template>
              <template v-slot:item="{ item }">
                {{$t(`gender.${item.id}`)}}
              </template>
            </v-select>
          </validation-provider>
          <validation-provider
            :name="$t((documentType !== null ? 'document_type.' + documentType : 'fields.document'))"
            rules="required"
            v-slot="{ errors }"
          >
            <v-text-field
              :label="$t((documentType !== null ? 'document_type.' + documentType : 'fields.document'))"
              v-model="documentId"
              filled
              required
              class="required"
              :error-messages="errors"
              :disabled="!isEditableField('document_id')"
            ></v-text-field>
          </validation-provider>
          <validation-provider :name="$t('fields.address')" v-slot="{ errors }">
            <v-text-field
              :label="$t('fields.address')"
              v-model="address"
              filled
              :error-messages="errors"
              :disabled="!isEditableField('address')"
            ></v-text-field>
          </validation-provider>
          <validation-provider immediate rules="required" :name="$t('fields.country')">
            <v-autocomplete
              :label="$t('fields.country')"
              v-model="country"
              :items="countryList"
              item-text="text"
              item-value="value"
              filled
              class="required"
              :disabled="!isEditableField('country')"
            ></v-autocomplete>
          </validation-provider>
          <validation-provider immediate rules="required" :name="$t('fields.nationality')">
            <v-autocomplete
              :label="$t('fields.nationality')"
              v-model="nationality"
              :items="countryList"
              item-text="text"
              item-value="value"
              filled
              class="required"
              :disabled="!isEditableField('nationality')"
            ></v-autocomplete>
          </validation-provider>
          <validation-provider immediate :name="$t('fields.place_of_birth')" v-slot="{ errors }">
            <v-text-field
              :label="$t('fields.place_of_birth')"
              v-model="placeOfBirth"
              filled
              :error-messages="errors"
              :disabled="!isEditableField('place_of_birth')"
            ></v-text-field>
          </validation-provider>
          <v-dialog
             ref="dialogDateOfExpiry"
             v-model="dateOfExpiryModal"
             :return-value.sync="dateOfExpiry"
             persistent
             width="290px"
           >
             <template v-slot:activator="{ on, attrs }">
               <validation-provider :name="$t('fields.document_expiration')" rules="required" v-slot="{ errors }">
                <v-text-field
                  :label="$t('fields.document_expiration')"
                  v-model="dateOfExpiryFormatted"
                  append-icon="mdi-calendar"
                  filled
                  readonly
                  required
                  class="required"
                  v-bind="attrs"
                  v-on="on"
                  :error-messages="errors"
                  :disabled="!isEditableField('date_of_expiry')"
                ></v-text-field>
              </validation-provider>
             </template>
             <v-date-picker v-model="dateOfExpiry" :locale="locale" :first-day-of-week="firstDayOfWeek" color="primary darken-1" scrollable>
               <v-spacer></v-spacer>
               <v-btn text color="primary darken-1" @click="dateOfExpiryModal = false">{{$t('actions.cancel')}}</v-btn>
               <v-btn text color="primary darken-1" @click="$refs.dialogDateOfExpiry.save(dateOfExpiry)">{{$t('actions.select')}}</v-btn>
             </v-date-picker>
          </v-dialog>
          <validation-provider :name="$t('fields.checkin_step2_confirm')" rules="required" v-slot="{ errors }">
            <div>
              <v-checkbox
                v-model="confirm"
                :label="$t('fields.checkin_step2_confirm')"
                color="primary darken-1"
                :error-messages="errors"
              >
              </v-checkbox>
            </div>
          </validation-provider>
        </v-form>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import { genders } from '@/config'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { mapGetters, mapState } from 'vuex'
import i18nCountries from 'i18n-iso-countries'
import moment from 'moment'
import { getDayOfWeek } from '@/lib/helpers/Time'
i18nCountries.registerLocale(require('i18n-iso-countries/langs/en.json'))
i18nCountries.registerLocale(require('i18n-iso-countries/langs/es.json'))
export default {
  props: {
    pendingVerification: Boolean,
  },
  data () {
    return {
      moment,
      genders,
      dateOfBirthModal: false,
      dateOfExpiryModal: false,
      confirm: false,
      validForm: false,
      countryList: [],
    }
  },
  components: {
    ValidationObserver,
    ValidationProvider,
  },
  computed: {
    locale () {
      return this.$i18n.locale
    },
    countryNames () {
      return i18nCountries.getNames(this.locale)
    },
    status () {
      if (this.registrationCards.scannedDocument !== null) {
        return this.registrationCards.scannedDocument.status
      } else {
        return 'IN_PROGRESS'
      }
    },
    inProgress () {
      if (this.pendingVerification || this.status === 'IN_PROGRESS') {
        return true
      } else {
        return false
      }
    },
    isCompleted () {
      if (!this.inProgress && (this.validForm) && this.confirm) {
        return true
      } else {
        return false
      }
    },
    name: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.name
          ? this.editedVerifiedDocumentData.name
          : this.verifiedDocumentData && this.verifiedDocumentData.name
            ? this.verifiedDocumentData.name
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'name', value })
      },
    },
    lastName: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.lastName
          ? this.editedVerifiedDocumentData.lastName
          : this.verifiedDocumentData && this.verifiedDocumentData.lastName
            ? this.verifiedDocumentData.lastName
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'lastName', value })
      },
    },
    dateOfBirth: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.dateOfBirth
          ? moment(this.editedVerifiedDocumentData.dateOfBirth).local().format('YYYY-MM-DD')
          : this.verifiedDocumentData && this.verifiedDocumentData.dateOfBirth
            ? moment(this.verifiedDocumentData.dateOfBirth).local().format('YYYY-MM-DD')
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', {
          field: 'dateOfBirth',
          value: value ? moment.utc(value, 'YYYY-MM-DD').add(12, 'h').format('YYYY-MM-DDTHH:mm:ss.SSSZZ') : null,
        })
      },
    },
    dateOfBirthFormatted () {
      return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.dateOfBirth
        ? moment(this.editedVerifiedDocumentData.dateOfBirth).local().format('DD/MM/YYYY')
        : this.verifiedDocumentData && this.verifiedDocumentData.dateOfBirth
          ? moment(this.verifiedDocumentData.dateOfBirth).local().format('DD/MM/YYYY')
          : null
    },
    dateOfExpiryFormatted () {
      return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.dateOfExpiry
        ? moment(this.editedVerifiedDocumentData.dateOfExpiry).local().format('DD/MM/YYYY')
        : this.verifiedDocumentData && this.verifiedDocumentData.dateOfExpiry
          ? moment(this.verifiedDocumentData.dateOfExpiry).local().format('DD/MM/YYYY')
          : null
    },
    gender: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.sex
          ? this.editedVerifiedDocumentData.sex.toLowerCase()
          : this.verifiedDocumentData && this.verifiedDocumentData.sex
            ? this.verifiedDocumentData.sex.toLowerCase()
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'sex', value: value.toUpperCase() })
      },
    },
    documentType () {
      return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.documentType
        ? this.editedVerifiedDocumentData.documentType
        : this.verifiedDocumentData && this.verifiedDocumentData.documentType
          ? this.verifiedDocumentData.documentType
          : null
    },
    documentId: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.documentId
          ? this.editedVerifiedDocumentData.documentId
          : this.verifiedDocumentData && this.verifiedDocumentData.documentId
            ? this.verifiedDocumentData.documentId
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'documentId', value })
      },
    },
    address: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.address
          ? this.editedVerifiedDocumentData.address
          : this.verifiedDocumentData && this.verifiedDocumentData.address
            ? this.verifiedDocumentData.address
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'address', value })
      },
    },
    dateOfExpiry: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.dateOfExpiry
          ? moment(this.editedVerifiedDocumentData.dateOfExpiry).local().format('YYYY-MM-DD')
          : this.verifiedDocumentData && this.verifiedDocumentData.dateOfExpiry
            ? moment(this.verifiedDocumentData.dateOfExpiry).local().format('YYYY-MM-DD')
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', {
          field: 'dateOfExpiry',
          value: value ? moment.utc(value, 'YYYY-MM-DD').add(12, 'h').format('YYYY-MM-DDTHH:mm:ss.SSSZZ') : null,
        })
      },
    },
    country: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.country
          ? this.editedVerifiedDocumentData.country
          : this.verifiedDocumentData && this.verifiedDocumentData.country
            ? this.verifiedDocumentData.country
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'country', value: value })
      },
    },
    nationality: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.nationality
          ? this.editedVerifiedDocumentData.nationality
          : this.verifiedDocumentData && this.verifiedDocumentData.nationality
            ? this.verifiedDocumentData.nationality
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'nationality', value: value })
      },
    },
    placeOfBirth: {
      get () {
        return !!this.editedVerifiedDocumentData && this.editedVerifiedDocumentData.placeOfBirth
          ? this.editedVerifiedDocumentData.placeOfBirth
          : this.verifiedDocumentData && this.verifiedDocumentData.placeOfBirth
            ? this.verifiedDocumentData.placeOfBirth
            : null
      },
      set (value) {
        this.$store.commit('registrationCards/setEditedVerifiedDocumentDataField', { field: 'placeOfBirth', value })
      },
    },
    firstDayOfWeek () {
      return getDayOfWeek(this.settings.webappUser.firstDayOfWeek)
    },
    ...mapState({
      editedVerifiedDocumentData: state => state.registrationCards.editedVerifiedDocumentData,
      verifiedDocumentData: state => state.registrationCards.verifiedDocumentData,
      registrationCards: state => state.registrationCards,
      scannedDocument: state => state.registrationCards.scannedDocument,
      settings: state => state.me.settings,
    }),
    ...mapGetters({
      editableFields: 'service/editableFields',
    }),
  },
  watch: {
    isCompleted: function (newValue, oldValue) {
      this.$emit('onIsCompletedChange', newValue)
    },
    inProgress: function (newValue, oldValue) {
      this.$emit('onSuccessChange', !newValue)
    },
    dateOfBirthModal (val) {
      val && setTimeout(() => (this.$refs.dateOfBirthPicker.activePicker = 'YEAR'))
    },
    locale: {
      immediate: true,
      handler: function () {
        moment.locale(this.locale)
      },
    },
  },
  created () {
    this.countryList = Object.keys(this.countryNames).map((key) => { return { value: i18nCountries.alpha2ToAlpha3(key), text: this.countryNames[key] } })
  },
  mounted () {
    this.$watch(
      () => {
        return this.$refs.step2Form.flags.valid
      },
      (val) => {
        this.validForm = val
      },
      {
        immediate: true,
      }
    )
  },
  methods: {
    isEditableField (field) {
      return this.editableFields.includes(field)
    },
  },
}
</script>

<style lang="scss">
.step-2 {
  text-align: center;

  .v-progress-circular {
    margin-bottom: 20px;
  }

  h1, p {
    color: rgba(0, 0, 0, 0.6);
    font-weight: 400;
  }

  h1 {
    font-size: 24px;
    line-height: 24px;
    margin-bottom: 15px;
  }

  p {
    line-height: 16px;
    margin-bottom: 0px;
  }
}
</style>
